import { Index, Show, type Accessor } from 'solid-js';
import type { Finding, Sev } from './health-derive';
import { overallStatus } from './health-derive';

/** Soft-background/foreground pair per severity — shared by the overall pill and the per-row glyph box. */
const SEV_SOFT: Record<Sev, string> = {
  crit: 'bg-crit-soft text-crit',
  warn: 'bg-warn-soft text-warn',
  good: 'bg-good-soft text-good',
};

/** Severity glyph — mirrors the mockup's `✕` / `!` / `✓` in `.fg`. */
const GLYPH: Record<Sev, string> = {
  crit: '✕',
  warn: '!',
  good: '✓',
};

/**
 * Live "what's happening right now" digest — matches the mockup's `.summary` section.
 *
 * Non-flicker is the whole point, hence `<Index>` rather than `<For>`: `deriveFindings` builds
 * a brand-new array every tick (it's a pure derivation re-run each poll), even when nothing
 * meaningful changed — e.g. only a detail number like "296 MB free" drifting to "301 MB free".
 * `<For>` keys rows by the array items themselves, so a new array means every row looks "new"
 * and gets torn down and recreated on every tick — that's the flicker. `<Index>` instead keys by
 * *position*: as long as the number of findings and their order stays the same, each `<li>` DOM
 * node is created once and stays mounted for the lifetime of that slot; only the reactive
 * expressions inside it (title/detail/action/sev, read via the row accessor `f()`) re-run and
 * patch the DOM in place when a value actually changes. That's a Solid-idiomatic version of the
 * mockup's hand-rolled `renderSummary`, which diffs a `sev|title` signature and only text-patches
 * `.fd` nodes when the signature is unchanged — Solid's fine-grained reactivity gives us the same
 * "patch in place, don't rebuild" behavior for free, keyed on list position rather than a manual
 * signature string.
 */
export function HealthSummary(props: {
  findings: Accessor<Finding[]>;
  /** True once at least one metrics source has produced a sample. Defaults to `true` so
   * existing call sites (tests, mainly) keep their prior "findings decide everything" behavior
   * — Layout is the one call site that can honestly answer `false`, on first paint. */
  hasTelemetry?: Accessor<boolean>;
  /** Seconds since the last telemetry frame of any kind arrived. Purely a freshness cue —
   * never used to decide what to render, only to show how current the render is. */
  updatedSecondsAgo?: Accessor<number>;
}) {
  const overall = () => overallStatus(props.findings());
  const hasTelemetry = () => props.hasTelemetry?.() ?? true;
  const agoLabel = () => `updated ${Math.max(0, props.updatedSecondsAgo?.() ?? 0)}s ago`;

  return (
    <Show
      when={props.findings().length > 0}
      fallback={
        <Show
          when={hasTelemetry()}
          fallback={
            <div class="hud-clip hud-mono mt-4 flex items-center gap-3 border border-line bg-panel px-5 py-3 text-[0.6875rem] uppercase tracking-[0.2em] text-muted">
              <span class="text-faint">…</span> Waiting for the first readings
            </div>
          }
        >
          <div class="hud-clip hud-mono mt-4 flex items-center gap-3 border border-line bg-panel px-5 py-3 text-[0.6875rem] uppercase tracking-[0.2em] text-muted">
            <span class="text-good">✓</span> All systems nominal
            <span class="ml-auto normal-case tracking-[0.08em] text-[0.625rem] text-faint">{agoLabel()}</span>
          </div>
        </Show>
      }
    >
      <section class="hud-clip mt-4 border border-line bg-panel">
        <div class="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <span class="hud-mono text-[0.6875rem] uppercase tracking-[0.2em] text-ink">
            What&apos;s happening right now
          </span>
          <span
            class={`hud-mono rounded-full px-2.5 py-1 text-[0.6563rem] uppercase tracking-[0.12em] ${
              SEV_SOFT[overall().level]
            }`}
          >
            {overall().label}
          </span>
          <span class="hud-mono ml-auto normal-case tracking-[0.08em] text-[0.625rem] text-faint">{agoLabel()}</span>
        </div>
        <ul class="m-0 flex list-none flex-col gap-0.5 p-2">
          <Index each={props.findings()}>
            {(f) => (
              <li class="flex items-start gap-2.5 px-2.5 py-2.5">
                <span
                  class={`mt-px grid h-5 w-5 flex-none place-items-center text-[0.72rem] font-extrabold ${
                    SEV_SOFT[f().sev]
                  }`}
                >
                  {GLYPH[f().sev]}
                </span>
                <div class="text-[0.85rem]">
                  <b class="text-ink">{f().title}</b>
                  <span class="text-muted"> — {f().detail}</span>
                  <Show when={f().action}>
                    <span class="mt-0.5 block text-[0.78rem] font-semibold text-holo">{f().action}</span>
                  </Show>
                </div>
              </li>
            )}
          </Index>
        </ul>
      </section>
    </Show>
  );
}
